export interface MeshPeer {
  name: string;
  stable_id?: string;
  peer_type?: string;
  label?: string;
  /** hub | peer | remote | … — z entity metadata, i pro uzly mimo topologii. */
  role?: string | null;
  host?: string;
  route_name?: string;
  is_local?: boolean;
  enabled?: boolean;
  last_seen?: string | null;
  online?: boolean;
  status?: 'online' | 'offline' | string;
  announce_age_s?: number | null;
  env?: string | null;
  public_ui?: string | null;
  public_ui_published?: boolean;
  public_codable?: string | null;
  public_codable_published?: boolean;
}

interface TopologyNode {
  id?: unknown;
  route_name?: unknown;
  label?: unknown;
  host?: unknown;
  health?: unknown;
  announce_age_s?: unknown;
  env?: unknown;
  public_ui?: unknown;
  public_ui_published?: unknown;
  public_codable?: unknown;
  public_codable_published?: unknown;
}

function topologyRows(raw: unknown): TopologyNode[] {
  if (!raw || typeof raw !== 'object') return [];
  const value = raw as { cluster?: unknown; remote?: unknown };
  return [
    ...(Array.isArray(value.cluster) ? value.cluster : []),
    ...(Array.isArray(value.remote) ? value.remote : []),
  ].filter((row): row is TopologyNode => !!row && typeof row === 'object');
}

/**
 * Peer inventory je transportní baseline; mesh.topology navíc drží explicitně
 * linked remote/spoke uzly i bez announce. Discovered nody se sem záměrně
 * nepřidávají, protože ještě nemají trust pro tool routing.
 */
/** Routing target for MCP: prefer stable_id (UUID), never display-only chaos. */
export function routingTarget(peer: Pick<MeshPeer, 'stable_id' | 'name'>): string {
  return (peer.stable_id || peer.name || '').trim();
}

export function looksNodeId(value: string | null | undefined): boolean {
  const s = (value ?? '').trim();
  if (!s) return true;
  if (s.startsWith('node:')) return true;
  if (/^spoke\s+acm-[0-9a-f]{8,}$/i.test(s)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ||
    /^acm-[0-9a-f]{8,}$/i.test(s);
}

function stripLocalSuffix(value: string): string {
  return value.replace(/\s*\((?:lokální|local)\)\s*$/i, '').trim();
}

/** Lidský název z jednoho inventáře — hostname/label, nikdy UUID. */
export function nodeDisplayLabel(peer: Pick<MeshPeer, 'label' | 'route_name' | 'host' | 'name' | 'stable_id'>): string {
  const candidates = [peer.label, peer.route_name, peer.host, peer.name, peer.stable_id];
  for (const raw of candidates) {
    const s = stripLocalSuffix((raw ?? '').trim());
    if (s && !looksNodeId(s)) return s;
  }
  const fallback = (peer.label || peer.route_name || peer.name || peer.stable_id || '').trim();
  return fallback || 'node';
}

export function resolveNodeDisplayLabel(
  value: string | null | undefined,
  peers: MeshPeer[],
  fallback?: string,
): string {
  if (!value) {
    const local = peers.find((peer) => peer.is_local);
    return local ? nodeDisplayLabel(local) : (fallback ?? 'Lokál');
  }
  const peer = peers.find((candidate) => peerMatchesKey(candidate, value));
  return peer ? nodeDisplayLabel(peer) : (fallback ?? value);
}

export function peerMatchesKey(peer: MeshPeer, key: string | null | undefined): boolean {
  if (!key) return false;
  return key === peer.name || key === peer.stable_id || key === peer.label ||
    key === peer.route_name || key === peer.host;
}

export type SwitchNode = {
  key: string;
  label: string;
  env?: string | null;
  url: string | null;
  current: boolean;
  /** Žádná veřejná UI URL — dostupný jen přes MCP z tohoto hosta. */
  intralink: boolean;
};

function toHttps(domain: string): string {
  const host = domain.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  return `https://${host}/`;
}

export function peerPublicUrl(
  peer: Pick<MeshPeer, 'public_ui' | 'public_ui_published' | 'public_codable' | 'public_codable_published'>,
  scope?: string | null,
): { url: string | null; published: boolean } {
  const domain = scope === 'codable' ? peer.public_codable : peer.public_ui;
  const published = scope === 'codable'
    ? peer.public_codable_published === true
    : peer.public_ui_published === true;
  const raw = typeof domain === 'string' ? domain.trim() : '';
  return { url: raw ? toHttps(raw) : null, published };
}

/** Všechny MCP-dosažitelné nody. Bez veřejné URL = intralink. */
/**
 * Interní fallback smí do rolety jen, když prohlížeč JE uvnitř sítě — tedy
 * když aktuální dashboard běží z privátní adresy/interního portu. Zvenku
 * (veřejná doména) by odkaz na https://10.x…:9080 vedl do prázdna.
 */
export function browserOnInternalNetwork(loc: { hostname: string; port: string }): boolean {
  const h = loc.hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]') return true;
  if (/^10\./.test(h) || /^192\.168\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
  return loc.port === '9080';
}

export function buildCallSwitchNodes(
  peers: MeshPeer[],
  opts: { callTarget: string | null; scope?: string | null; allowInternal?: boolean },
): SwitchNode[] {
  const out: SwitchNode[] = [];
  const seen = new Set<string>();
  for (const peer of peers) {
    if (peer.enabled === false) continue;
    const key = routingTarget(peer);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const pub = peerPublicUrl(peer, opts.scope);
    // Bez veřejné URL fallback na INTERNÍ dashboard uzlu (https://<host>:9080).
    // Produkční spoky veřejnou UI nemají a klik v roletě byl tiché nic
    // (Filip 2026-09-02: „bere jen veřejné URL a nefallbackne na privátní").
    // Session-hop na interní adrese funguje stejně; uvnitř sítě je dosažitelná.
    const internalUrl =
      !pub.url && opts.allowInternal === true && typeof peer.host === 'string' &&
        peer.host.trim()
        ? `https://${peer.host.trim()}:9080/`
        : null;
    const current = opts.callTarget
      ? peerMatchesKey(peer, opts.callTarget)
      : peer.is_local === true;
    out.push({
      key,
      label: nodeDisplayLabel(peer),
      env: peer.env ?? null,
      url: pub.url ?? internalUrl,
      current,
      intralink: !pub.url,
    });
  }
  out.sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  return out;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return url.replace(/^https?:\/\//i, '').split('/')[0]?.toLowerCase() ?? '';
  }
}

export type HomeUiTarget = {
  url: string | null;
  published: boolean;
  label: string | null;
};

function asHttps(domain: string): string {
  const raw = domain.replace(/^https?:\/\//i, '').replace(/\/$/, '').trim();
  return raw ? `https://${raw}/` : '';
}

/** Veřejné UI uzlu, se kterým tenhle (typicky spoke) komunikuje. */
export function resolveHomeUi(opts: {
  spokeHub?: string | null;
  peers: MeshPeer[];
  scope?: string | null;
  explicitUrl?: string | null;
  explicitPublished?: boolean;
  explicitLabel?: string | null;
  /** Prohlížeč je uvnitř sítě → domeček smí vést i na interní :9080 hubu. */
  allowInternal?: boolean;
}): HomeUiTarget {
  const hub = (opts.spokeHub ?? '').trim();
  const explicit = (opts.explicitUrl ?? '').trim();
  if (explicit) {
    return {
      url: asHttps(explicit) || null,
      published: opts.explicitPublished === true,
      label: opts.explicitLabel ?? (hub || null),
    };
  }
  if (!hub) return { url: null, published: false, label: null };
  const peer = opts.peers.find((p) => !p.is_local && peerMatchesKey(p, hub));
  if (!peer) return { url: null, published: false, label: hub };
  const pub = peerPublicUrl(peer, opts.scope);
  // Hub bez publikované veřejné UI = mrtvý šedý domeček na remote nodech
  // (Filip 2026-09-02: „myslel jsem, že se dá klepnout… nedá"). Uvnitř sítě
  // vede domeček na interní dashboard hubu.
  const internal =
    !pub.url && opts.allowInternal === true && typeof peer.host === 'string' && peer.host.trim()
      ? `https://${peer.host.trim()}:9080/`
      : null;
  return {
    url: pub.url ?? internal,
    published: pub.published || Boolean(internal),
    label: nodeDisplayLabel(peer),
  };
}

export function homeUiHref(home: HomeUiTarget): string | null {
  if (!home.url || !home.published) return null;
  return home.url;
}

/** Jen nody s publikovanou veřejnou UI URL — kompletní přepnutí stránky. */
export function buildUiSwitchNodes(
  peers: MeshPeer[],
  opts: {
    hereHost: string;
    hereUrl: string;
    scope?: string | null;
    allowInternal?: boolean;
    /** Jméno tohoto uzlu (server-info hostname) — v roletě NIKDY holá IP. */
    hereLabel?: string | null;
  },
): SwitchNode[] {
  const hereHost = opts.hereHost.toLowerCase();
  const out: SwitchNode[] = [];
  const seen = new Set<string>();

  const add = (peer: MeshPeer | null, fallbackUrl?: string) => {
    const key = peer ? routingTarget(peer) : 'current';
    if (!key || seen.has(key)) return;
    const pub = peer ? peerPublicUrl(peer, opts.scope) : { url: null, published: false };
    // Uzel bez publikované veřejné UI se dřív z rolety ZAHODIL — produkční
    // spoky žádnou nemají a roleta byla poloprázdná (Filip 2026-09-02).
    // Fallback na interní https://<host>:9080 — ale JEN když je prohlížeč
    // sám uvnitř sítě (allowInternal z browserOnInternalNetwork), zvenku by
    // odkaz na privátní IP vedl do prázdna.
    const internalUrl =
      opts.allowInternal === true && peer && !peer.is_local &&
        typeof peer.host === 'string' && peer.host.trim()
        ? `https://${peer.host.trim()}:9080/`
        : null;
    const url = (pub.url && pub.published)
      ? pub.url
      : peer?.is_local
      ? (fallbackUrl || opts.hereUrl)
      : internalUrl;
    if (!url) return;
    seen.add(key);
    out.push({
      key,
      label: peer ? nodeDisplayLabel(peer) : hereHost,
      env: peer?.env ?? null,
      url,
      current: peer?.is_local === true || hostOf(url) === hereHost,
      intralink: !(pub.url && pub.published) && !peer?.is_local,
    });
  };

  const local = peers.find((p) => p.is_local) ?? null;
  if (local) add(local, opts.hereUrl);
  else {
    seen.add('current');
    out.push({
      key: 'current',
      // Přístup přes IP nesmí ukázat IP jako jméno uzlu — vzít hostname ze
      // server-info (Filip 2026-09-02: „lokální IP překládat na jména").
      label: (opts.hereLabel ?? '').trim() || hereHost,
      env: null,
      url: opts.hereUrl,
      current: true,
      intralink: false,
    });
  }

  for (const peer of peers) {
    if (peer.enabled === false || peer.is_local) continue;
    add(peer);
  }
  return out;
}

function humanOrEmpty(...values: Array<string | null | undefined>): string {
  for (const raw of values) {
    const s = stripLocalSuffix((raw ?? '').trim());
    if (s && !looksNodeId(s)) return s;
  }
  return '';
}

export function mergeReachableNodeTargets(peers: MeshPeer[], topology: unknown): MeshPeer[] {
  const result: MeshPeer[] = peers.map((peer) => ({
    ...peer,
    name: (peer.stable_id || peer.name || '').trim(),
    stable_id: peer.stable_id || peer.name,
    route_name: peer.route_name,
    label: humanOrEmpty(peer.label, peer.route_name, peer.host) || peer.label || peer.name,
  }));
  for (const row of topologyRows(topology)) {
    const routeName = String(row.route_name ?? '').trim();
    const stableId = String(row.id ?? '').trim();
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    // Wire key: stable_id first so tool target_node always works with Phase 14 identity
    const name = stableId || routeName;
    if (!name) continue;
    const existing = result.find((peer) =>
      peerMatchesKey(peer, name) ||
      peerMatchesKey(peer, routeName) ||
      peerMatchesKey(peer, stableId) ||
      peerMatchesKey(peer, label)
    );
    const health = typeof row.health === 'string' ? row.health : undefined;
    const sid = stableId || name;
    const publicUi = typeof row.public_ui === 'string' ? row.public_ui : undefined;
    const publicCodable = typeof row.public_codable === 'string' ? row.public_codable : undefined;
    const overlay: MeshPeer = {
      // name = routing key (stable_id preferred)
      name: sid,
      stable_id: sid,
      route_name: routeName || undefined,
      label: humanOrEmpty(label, routeName) || label || routeName || name,
      host: typeof row.host === 'string' ? row.host : undefined,
      enabled: true,
      online: health === 'online' ? true : health === 'offline' ? false : undefined,
      status: health,
      announce_age_s: typeof row.announce_age_s === 'number' ? row.announce_age_s : null,
      env: typeof row.env === 'string' ? row.env : undefined,
      public_ui: publicUi,
      public_ui_published: typeof row.public_ui_published === 'boolean'
        ? row.public_ui_published
        : undefined,
      public_codable: publicCodable,
      public_codable_published: typeof row.public_codable_published === 'boolean'
        ? row.public_codable_published
        : undefined,
    };
    if (existing) {
      Object.assign(existing, overlay, {
        is_local: existing.is_local,
        label: humanOrEmpty(label, routeName, existing.label) || overlay.label || existing.label,
        route_name: routeName || existing.route_name,
        host: overlay.host || existing.host,
        env: overlay.env ?? existing.env,
        public_ui: overlay.public_ui ?? existing.public_ui,
        public_ui_published: overlay.public_ui_published ?? existing.public_ui_published,
        public_codable: overlay.public_codable ?? existing.public_codable,
        public_codable_published: overlay.public_codable_published ??
          existing.public_codable_published,
      });
    } else {
      result.push(overlay);
    }
  }
  // Collapse any residual duplicates (same stable_id under two names)
  const bySid = new Map<string, MeshPeer>();
  for (const p of result) {
    const k = (p.stable_id || p.name || '').trim();
    if (!k) continue;
    const prev = bySid.get(k);
    if (!prev) {
      bySid.set(k, { ...p, name: k, stable_id: k, label: nodeDisplayLabel(p) });
      continue;
    }
    const merged: MeshPeer = {
      ...prev,
      ...p,
      name: k,
      stable_id: k,
      is_local: prev.is_local || p.is_local,
      route_name: p.route_name || prev.route_name,
      host: p.host || prev.host,
      env: p.env ?? prev.env,
      public_ui: p.public_ui ?? prev.public_ui,
      public_ui_published: p.public_ui_published ?? prev.public_ui_published,
      public_codable: p.public_codable ?? prev.public_codable,
      public_codable_published: p.public_codable_published ?? prev.public_codable_published,
    };
    merged.label = nodeDisplayLabel(merged);
    bySid.set(k, merged);
  }
  return [...bySid.values()];
}
